import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListVendorBills,
  useListInventoryVendors,
  useGetVendorBill,
  useSubmitVendorBill,
  useApproveVendorBill,
  useRejectVendorBill,
  useGetLedgerConfig,
  getListVendorBillsQueryKey,
  getGetVendorBillQueryKey,
} from "@workspace/api-client-react";
import type { VendorBill, VendorBillDetail, VendorBillStatus } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ListPagination } from "@/components/common/list-pagination";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth-context";
import { inr } from "@/lib/format";
import { venId } from "@/lib/display-id";
import { BillEditorSheet } from "@/components/expenses/bill-editor-sheet";
import { BillDetailSheet } from "@/components/expenses/bill-detail-sheet";
import { PaymentDialog } from "@/components/expenses/payment-dialog";

export const STATUS_LABEL: Record<VendorBillStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending approval",
  approved: "Approved",
  partially_paid: "Partially paid",
  paid: "Paid",
  rejected: "Rejected",
  void: "Void (reversed)",
};

const STATUS_BADGE_VARIANT: Record<VendorBillStatus, "default" | "secondary" | "outline" | "destructive"> = {
  draft: "outline",
  pending_approval: "secondary",
  approved: "secondary",
  partially_paid: "secondary",
  paid: "default",
  rejected: "destructive",
  void: "destructive",
};

/** Locked_ux's Treatment badge — Prepaid overrides the underlying tax
 * treatment in the badge (a prepaid bill is still e.g. "registered" for GST
 * purposes, but the operator-facing label the badge shows is "Prepaid"). */
export function billTreatmentLabel(bill: Pick<VendorBill, "taxTreatment" | "isPrepaid">): string {
  if (bill.isPrepaid) return "Prepaid";
  switch (bill.taxTreatment) {
    case "registered":
      return "Registered";
    case "unregistered":
      return "Unregistered";
    case "rcm":
      return "RCM";
    case "itc_blocked":
      return "ITC-blocked";
    default:
      return bill.taxTreatment;
  }
}

/**
 * `GET /vendor-bills` (the list endpoint) returns the bill header only — no
 * per-bill payment aggregate — so an exact Paid/Outstanding split for a
 * `partially_paid` row is not derivable without an N+1 fetch per row (which
 * would mean either a hand-written fetch loop or one `useGetVendorBill` call
 * per row, neither acceptable). Draft/pending/rejected/void bills have no AP
 * posted at all yet, so both columns are correctly "—" for them; approved
 * and paid are exact from the header alone. `partially_paid` says so
 * honestly rather than fabricating a number — open the row for the real
 * split from its payments list.
 */
function paidOutstandingLabels(bill: Pick<VendorBill, "status" | "grossTotal">): { paidLabel: string; outstandingLabel: string } {
  switch (bill.status) {
    case "approved":
      return { paidLabel: inr(0), outstandingLabel: inr(bill.grossTotal) };
    case "paid":
      return { paidLabel: inr(bill.grossTotal), outstandingLabel: inr(0) };
    case "partially_paid":
      return { paidLabel: "Partially paid — open for split", outstandingLabel: "Partially paid — open for split" };
    default:
      return { paidLabel: "—", outstandingLabel: "—" };
  }
}

interface Filters {
  status: VendorBillStatus | "all";
  vendorId: number | "all";
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { status: "all", vendorId: "all", from: "", to: "" };

type Panel = { kind: "create" } | { kind: "view"; id: number } | { kind: "edit"; id: number } | { kind: "pay"; id: number } | null;

export interface BillListProps {
  /** Set by the Schedules tab's "Generate bills" toast action — pre-filters
   * to zero-total draft bills (57-15's "incomplete" count made actionable). */
  zeroTotalDraftsSignal?: number;
}

export function BillList({ zeroTotalDraftsSignal }: BillListProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [zeroTotalDraftsOnly, setZeroTotalDraftsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [panel, setPanel] = useState<Panel>(null);
  const [rejectTarget, setRejectTarget] = useState<VendorBill | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // The Schedules tab bumps this signal (a plain counter) after a "Generate
  // bills" run reports incomplete > 0 — switch to the zero-total-drafts view
  // so the operator lands directly on the rows that need attention.
  useEffect(() => {
    if (zeroTotalDraftsSignal !== undefined) {
      setZeroTotalDraftsOnly(true);
      setFilters((f) => ({ ...f, status: "draft" }));
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeroTotalDraftsSignal]);

  const { data: vendors } = useListInventoryVendors({});
  const { data: config } = useGetLedgerConfig();

  const { data, isLoading } = useListVendorBills({
    status: filters.status === "all" ? undefined : filters.status,
    vendorId: filters.vendorId === "all" ? undefined : filters.vendorId,
    from: filters.from || undefined,
    to: filters.to || undefined,
    page,
    pageSize,
  });

  const detailBillId = panel && panel.kind !== "create" ? panel.id : 0;
  const detailQuery = useGetVendorBill(detailBillId, {
    query: { enabled: !!panel && panel.kind !== "create", queryKey: getGetVendorBillQueryKey(detailBillId) },
  });
  const detail: VendorBillDetail | null = panel?.kind === "create" ? null : (detailQuery.data ?? null);

  const submitMut = useSubmitVendorBill();
  const approveMut = useApproveVendorBill();
  const rejectMut = useRejectVendorBill();

  const vendorName = (id: number) => vendors?.find((v) => v.id === id)?.vendorName ?? `Vendor ${id}`;

  const allRows = data?.items ?? [];
  const rows = zeroTotalDraftsOnly ? allRows.filter((b) => b.status === "draft" && b.grossTotal === 0) : allRows;

  function refreshList() {
    qc.invalidateQueries({ queryKey: getListVendorBillsQueryKey() });
  }

  async function handleSubmit(bill: VendorBill) {
    try {
      await submitMut.mutateAsync({ id: bill.id });
      refreshList();
      toast({ title: `Bill #${bill.id} submitted for approval.` });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not submit.", variant: "destructive" });
    }
  }

  function approveGate(bill: VendorBill): { allowed: boolean; tooltip: string | null } {
    if (user?.role === "super_admin") return { allowed: true, tooltip: null };
    const threshold = config?.billApprovalThreshold;
    if (threshold == null) {
      return { allowed: false, tooltip: "Requires super-admin approval — no threshold is configured for this space yet." };
    }
    if (bill.grossTotal > threshold) {
      return { allowed: false, tooltip: `Requires super-admin approval — exceeds this space's threshold of ${inr(threshold)}.` };
    }
    // Best-effort only: this client has no visibility into the per-user
    // `can_approve_bills` flag (no generated read/write surface exists for
    // it yet — see 57-16-SUMMARY.md's deviations). The server remains the
    // real gate; a user within the threshold who still lacks the flag gets
    // a clear 403 toast from the mutation's own catch block below, not a
    // silently wrong "allowed".
    return { allowed: true, tooltip: null };
  }

  async function handleApprove(bill: VendorBill) {
    try {
      const result = await approveMut.mutateAsync({ id: bill.id, data: {} });
      refreshList();
      const withQ = result as VendorBillDetail & { quarantined?: boolean; reason?: string };
      if (withQ.quarantined) {
        toast({ title: `Bill #${bill.id} approved — posting quarantined`, description: withQ.reason ?? undefined, variant: "destructive" });
      } else {
        toast({ title: `Bill #${bill.id} approved and posted.` });
      }
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not approve.", variant: "destructive" });
    }
  }

  async function submitReject() {
    if (!rejectTarget || !rejectReason.trim()) {
      toast({ title: "A reason is required.", variant: "destructive" });
      return;
    }
    try {
      await rejectMut.mutateAsync({ id: rejectTarget.id, data: { reason: rejectReason.trim() } });
      refreshList();
      toast({ title: `Bill #${rejectTarget.id} rejected.` });
      setRejectTarget(null);
      setRejectReason("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not reject.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold">Status</label>
          <Select value={filters.status} onValueChange={(v) => { setFilters((f) => ({ ...f, status: v as Filters["status"] })); setPage(1); }}>
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(Object.keys(STATUS_LABEL) as VendorBillStatus[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold">Vendor</label>
          <Select
            value={filters.vendorId === "all" ? "all" : String(filters.vendorId)}
            onValueChange={(v) => { setFilters((f) => ({ ...f, vendorId: v === "all" ? "all" : Number(v) })); setPage(1); }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vendors</SelectItem>
              {(vendors ?? []).map((v) => (
                <SelectItem key={v.id} value={String(v.id)}>
                  {venId(v.id)} · {v.vendorName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold">From</label>
          <Input type="date" className="w-[150px]" value={filters.from} onChange={(e) => { setFilters((f) => ({ ...f, from: e.target.value })); setPage(1); }} />
        </div>
        <div>
          <label className="text-xs font-semibold">To</label>
          <Input type="date" className="w-[150px]" value={filters.to} onChange={(e) => { setFilters((f) => ({ ...f, to: e.target.value })); setPage(1); }} />
        </div>
        {zeroTotalDraftsOnly && (
          <Button size="sm" variant="outline" onClick={() => setZeroTotalDraftsOnly(false)}>
            Clear "needs mapping" filter
          </Button>
        )}
        <div className="flex-1" />
        <Button onClick={() => setPanel({ kind: "create" })}>New Bill</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill #</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Bill date</TableHead>
                <TableHead>Due date</TableHead>
                <TableHead>Treatment</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(10)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                    No bills match these filters. Click "New Bill" to add one.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((bill) => {
                  const gate = approveGate(bill);
                  const { paidLabel, outstandingLabel } = paidOutstandingLabels(bill);
                  return (
                    <TableRow key={bill.id} className="cursor-pointer" onClick={() => setPanel({ kind: "view", id: bill.id })}>
                      <TableCell className="font-medium">Bill #{bill.id}</TableCell>
                      <TableCell className="text-sm">
                        {venId(bill.vendorId)} · {vendorName(bill.vendorId)}
                      </TableCell>
                      <TableCell className="text-sm">{bill.billDate.slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{bill.dueDate ? bill.dueDate.slice(0, 10) : "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{billTreatmentLabel(bill)}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{inr(bill.grossTotal)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{paidLabel}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{outstandingLabel}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[bill.status]}>{STATUS_LABEL[bill.status]}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                        {bill.status === "draft" && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setPanel({ kind: "edit", id: bill.id })}>
                              Edit
                            </Button>
                            <Button size="sm" onClick={() => handleSubmit(bill)} disabled={submitMut.isPending}>
                              Submit
                            </Button>
                          </>
                        )}
                        {bill.status === "pending_approval" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(bill)}
                              disabled={!gate.allowed || approveMut.isPending}
                              title={gate.tooltip ?? undefined}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              disabled={!gate.allowed}
                              title={gate.tooltip ?? undefined}
                              onClick={() => setRejectTarget(bill)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {(bill.status === "approved" || bill.status === "partially_paid") && (
                          <Button size="sm" onClick={() => setPanel({ kind: "pay", id: bill.id })}>
                            Record payment
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {!isLoading && data && data.total > 0 && !zeroTotalDraftsOnly ? (
            <ListPagination
              total={data.total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
              noun={["bill", "bills"]}
            />
          ) : null}
        </CardContent>
      </Card>

      <BillEditorSheet
        open={panel?.kind === "create" || (panel?.kind === "edit" && !!detail)}
        onOpenChange={(o) => {
          if (!o) setPanel(null);
        }}
        bill={panel?.kind === "create" ? null : detail}
      />
      <BillDetailSheet
        open={panel?.kind === "view" && !!detail}
        onOpenChange={(o) => {
          if (!o) setPanel(null);
        }}
        bill={panel?.kind === "view" ? detail : null}
        onRecordPayment={(b) => setPanel({ kind: "pay", id: b.id })}
      />
      <PaymentDialog
        open={panel?.kind === "pay" && !!detail}
        onOpenChange={(o) => {
          if (!o) setPanel(null);
        }}
        bill={panel?.kind === "pay" ? detail : null}
      />

      <Dialog open={rejectTarget !== null} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject bill #{rejectTarget?.id}?</DialogTitle>
          </DialogHeader>
          <div>
            <label className="text-xs font-semibold">
              Reason <span className="text-destructive">*</span>
            </label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitReject} disabled={rejectMut.isPending}>
              {rejectMut.isPending ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
